/* eslint-disable prefer-const */
/* eslint-disable semi */
// OnlineDataProvider - Updated to use unified WebSocket service
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  handleOrderUpdater,
  handleWalletUpdater,
  handleOnlineUser,
  handleBANKCARDUpdater,
} from "@store/notification";
import { useFetchOrder } from "@hooks/useReactQuery";
import { useFetchWallet } from "../utility/hooks/useReactQuery";
import { useWalletCacheManager } from "../utility/hooks/useWalletCacheManager";
import { useCoinPrice } from "../utility/hooks/coins/usePrice";
import { handleBankCardData } from "../redux/bankCard";
import webSocketService from "../services/WebSocketService";
import cacheUpdateService from "../services/CacheUpdateService";
import { useWebSocketState } from "../hooks/useWebSocketState";
import { showGenericNotification, isDesktopNotificationsEnabled } from "../utils/browserNotifications";
import { useQueryClient } from "react-query";
import toastManager from "../utils/toastManager";

export const OnlineDataProvider = ({ children }) => {
  const [orderQuery, setOrderQuery] = useState({ page: 1, query: "" });
  const {
    data: orders,
    error,
    isLoading: isOrdersLoading,
    refetch: refetchOrder,
  } = useFetchOrder(orderQuery.page, orderQuery.query);
  const {
    data: walletList,
    isLoading: isProfileLoading,
    refetch: refetchWallet,
  } = useFetchWallet();

  // Use new WebSocket state management
  const { isNotificationsConnected, notifications } = useWebSocketState();
  const { invalidateBasedOnOperation } = useWalletCacheManager();
  const auth = useSelector((state) => state.auth);
  const orderUpdater = useSelector((state) => state.websocket.orderData.updateTrigger);
  const dispatch = useDispatch();
  const coinsPrices = useCoinPrice();
  const queryClient = useQueryClient();

  // Track component mount state
  const isMountedRef = useRef(true);

  // Handle connection state changes from unified WebSocket service
  useEffect(() => {
    isMountedRef.current = true;

    // Update online status based on notifications connection
    dispatch(handleOnlineUser(isNotificationsConnected));

    return () => {
      isMountedRef.current = false;
    };
  }, [isNotificationsConnected, dispatch]);

  // Track which notifications have been shown as toasts to prevent duplicates
  const shownNotificationIds = useRef(new Set());

  // Track when the session started to avoid showing old notifications
  // Use sessionStorage to persist across component remounts but reset on page refresh
  const getSessionStartTime = () => {
    const stored = sessionStorage.getItem('bitpanel_session_start');
    if (stored) {
      return parseInt(stored, 10);
    }
    const now = Date.now();
    sessionStorage.setItem('bitpanel_session_start', now.toString());
    return now;
  };

  const sessionStartTimeRef = useRef(getSessionStartTime());
  const sessionStartTime = sessionStartTimeRef.current;

  // Handle notification messages from Redux state
  useEffect(() => {
    if (!notifications.messages.length) {
      console.log('⚠️ No notification messages in Redux state');
      return;
    }

    // Get the latest unread notification that hasn't been shown yet
    // AND was created AFTER this session started (to avoid showing old notifications on login)
    const latestNotification = notifications.messages.find(msg => {
      if (msg.read || shownNotificationIds.current.has(msg.id)) {
        return false;
      }

      // Check if notification was created after session started
      // Use created_at if available (from normalized notification), otherwise fall back to timestamp
      const notificationTime = msg.created_at
        ? new Date(msg.created_at).getTime()
        : (msg.timestamp || 0);

      const isNewNotification = notificationTime > sessionStartTime;

      console.log('🔍 Checking notification:', {
        id: msg.id,
        title: msg.title?.substring(0, 50),
        created_at: msg.created_at,
        timestamp: msg.timestamp,
        notificationTime,
        sessionStartTime,
        isNew: isNewNotification,
        timeDiff: notificationTime - sessionStartTime
      });

      return isNewNotification;
    });

    if (!latestNotification) {
      console.log('⚠️ No new unread notifications found. Total messages:', notifications.messages.length);
      return;
    }

    console.log('✅ Found notification to show:', {
      id: latestNotification.id,
      title: latestNotification.title,
      created_at: latestNotification.created_at,
      timestamp: latestNotification.timestamp
    });

    const data = latestNotification;

    // Mark this notification as shown to prevent duplicate toasts
    shownNotificationIds.current.add(data.id);

    // Clean up old IDs (keep only last 100 to prevent memory leak)
    if (shownNotificationIds.current.size > 100) {
      const idsArray = Array.from(shownNotificationIds.current);
      const recentIds = idsArray.slice(-100);
      shownNotificationIds.current = new Set(recentIds);
    }

    console.log('📨 Processing notification from unified WebSocket service:', data);
    console.log('🔍 Notification has flags:', {
      update_wallet: data.update_wallet,
      update_orders: data.update_orders,
      silent: data.silent
    });

    // Normalize type to handle both string and numeric types
    const messageType = typeof data.type === 'string'
      ? data.type.toUpperCase()
      : data.type;

    console.log('🔍 Processing message type:', messageType, '(original:', data.type, ')');

    // ✅ NEW: Handle update_wallet and update_orders flags (unified notification approach)
    // These flags allow a single notification to trigger multiple cache updates
    const shouldUpdateWallet = data.update_wallet === true;
    const shouldUpdateOrders = data.update_orders === true;
    const isSilent = data.silent === true;

    console.log('🔍 Notification flags:', {
      type: messageType,
      update_wallet: shouldUpdateWallet,
      update_orders: shouldUpdateOrders,
      silent: isSilent
    });

    // Handle wallet updates (either from type=1 OR update_wallet flag)
    if (messageType === 'WALLET' || data.type === 1 || shouldUpdateWallet) {
      console.log('🔄 Wallet update triggered - using smart cache update');
      console.log('💰 Wallet data:', data);
      cacheUpdateService.invalidateForOperation('WEBSOCKET_UPDATE', {
        type: 'wallet',
        data: data
      });
      dispatch(handleWalletUpdater());
    }

    // Handle order updates (either from type=2 OR update_orders flag)
    if (messageType === 'ORDER' || data.type === 2 || shouldUpdateOrders) {
      console.log('🔄 Order update triggered - using smart cache update');
      console.log('📋 Order data:', data);
      cacheUpdateService.invalidateForOperation('WEBSOCKET_UPDATE', {
        type: 'order',
        data: data
      });
      dispatch(handleOrderUpdater());
    }

    // Handle notification display
    // Show toast ONLY if: type=0 AND silent=false
    if ((messageType === 'NOTIFICATION' || data.type === 0) && !isSilent) {
      // General notification - show toast using enhanced toastManager
      console.log('🔔 Showing notification toast (type=0, silent=false)');

      // 🔥 PHASE 0: Use normalized 'message' field instead of 'content'
      // Normalized notifications always use 'message' field (not 'content')
      toastManager.showSuccess(
        <div>
          <h5>{data.title}</h5>
          <p>{data.message}</p>
        </div>
      );

      // Show desktop notification if enabled
      // 🔥 PHASE 0: Use normalized 'message' field instead of 'content'
      if (isDesktopNotificationsEnabled()) {
        showGenericNotification(data.title, data.message, data.section);
      }

      // ✅ Phase 2: Invalidate React Query notification cache for instant updates
      console.log('🔄 Invalidating notification cache for real-time updates');
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['unreadNotifications']);

      // Handle specific sections
      if (data.section === "BANK_CARD" && window.location.pathname === "/addcard") {
        dispatch(handleBankCardData());
      }

      // FIXED: Use React Query invalidation instead of hard reload
      if (data.section === "TICKET") {
        console.log('🔄 Ticket notification received - invalidating ticket queries');
        queryClient.invalidateQueries(['tickets']);
        queryClient.invalidateQueries(['supportTickets']);
      }
    } else if ((messageType === 'NOTIFICATION' || data.type === 0) && isSilent) {
      console.log('🔕 Silent notification (type=0, silent=true) - skipping toast display');
    }
  }, [notifications.messages, dispatch]);

  // Legacy order refetch - this will be replaced by smart cache updates
  useEffect(() => {
    if (orderUpdater && isMountedRef.current) {
      console.log('🔄 Order updater triggered - refetching orders (legacy behavior)');
      refetchOrder();
    }
  }, [orderUpdater, refetchOrder]);

  return null;
};
